import { EntityFieldCodeEnum, type CustomFieldValueData } from '@/common/types/fields'
import { MainEntityFieldInjectionMethods } from '@/common/types/general'

export type BaseCustomerDTO = {
  // basic fields without include or expand
  id: number
  reference?: string
  title?: string
  firstName?: string
  lastName?: string
  nir?: string
  vip?: boolean
  external?: boolean
  companyName?: string
  companyPosition?: string
  fullName?: string
  // include COMMENTS
  comments?: string
  // include BIRTH_DATA
  birthCity?: string
  birthDate?: string
  birthDepartment?: string
  birthName?: string
  // include POSTAL_ADDRESS
  address1?: string
  address2?: string
  address3?: string
  address4?: string
  address5?: string
  city?: string
  zipCode?: string
  country?: string
  // include AUDIT_DATES
  creationDate?: string
  lastModifiedDate?: string
  deleteDate?: string
  deletionJobExecutionDate?: string
  // include AUDIT_USERS
  creator?: string
  updateUser?: string
  deleteUser?: string
  // expand EMAIL_ADDRESSES
  emailAddresses?: string[]
  // expand CUSTOM_FIELDS
  customFields?: CustomFieldValueData[]
}

// Bulk fetch V1
export type CustomerDetailsDTOV1 = BaseCustomerDTO & {
  // expand PHONE_NUMBERS
  phoneNumbers?: string[]
}

// V3 DTO
export type CustomerDTOV3 = BaseCustomerDTO & {
  // expand PHONE_NUMBERS
  phoneNumbers?: PhoneNumber[]
}

export type Customer = (CustomerDTOV3 | CustomerDetailsDTOV1) & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export type PhoneNumber = {
  fieldCode: string
  phoneNumber: string
}

export enum CustomerIncludeEnum {
  COMMENTS = 'COMMENTS',
  BIRTH_DATA = 'BIRTH_DATA',
  POSTAL_ADDRESS = 'POSTAL_ADDRESS',
  AUDIT_DATES = 'AUDIT_DATES',
  AUDIT_USERS = 'AUDIT_USERS',
}

export enum CustomerExpandEnum {
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
  PHONE_NUMBERS = 'PHONE_NUMBERS',
  EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
}

type CustomerAvailableIncludeOptions = keyof typeof CustomerIncludeEnum
type CustomerAvailableExpandOptions = keyof typeof CustomerExpandEnum

type CustomerFieldInjectionMethod<T> = T extends CustomerAvailableExpandOptions
  ? (typeof MainEntityFieldInjectionMethods)['EXPAND']
  : (typeof MainEntityFieldInjectionMethods)['INCLUDE']

type CustomerFieldInjectionOption<T extends CustomerAvailableIncludeOptions | CustomerAvailableExpandOptions> = {
  scope: T
  method: CustomerFieldInjectionMethod<T>
}

/**
 * Map of customer fields added by an include and an expand
 */
export const CustomerFieldInjectionMap = new Map<
  EntityFieldCodeEnum,
  CustomerFieldInjectionOption<CustomerAvailableIncludeOptions> | CustomerFieldInjectionOption<CustomerAvailableExpandOptions>
>([
  [EntityFieldCodeEnum.CUSTOMER_COMMENTS, { scope: CustomerIncludeEnum.COMMENTS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHCITY, { scope: CustomerIncludeEnum.BIRTH_DATA, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDATE, { scope: CustomerIncludeEnum.BIRTH_DATA, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHDEPARTMENT, { scope: CustomerIncludeEnum.BIRTH_DATA, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_BIRTHNAME, { scope: CustomerIncludeEnum.BIRTH_DATA, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS1, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS2, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS3, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS4, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS5, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CITY, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_ZIPCODE, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_COUNTRY, { scope: CustomerIncludeEnum.POSTAL_ADDRESS, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_CREATIONDATE, { scope: CustomerIncludeEnum.AUDIT_DATES, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_LAST_MODIFIED_DATE, { scope: CustomerIncludeEnum.AUDIT_DATES, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CUSTOMER_DELETEDATE, { scope: CustomerIncludeEnum.AUDIT_DATES, method: MainEntityFieldInjectionMethods.INCLUDE }],
  [
    EntityFieldCodeEnum.CUSTOMER_DELETION_JOB_EXECUTION_DATE,
    { scope: CustomerIncludeEnum.AUDIT_DATES, method: MainEntityFieldInjectionMethods.INCLUDE },
  ],
  [EntityFieldCodeEnum.CUSTOMER_PHONE, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_MOBILEPHONE, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_HOMEPHONE, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_WORKPHONE, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_OTHERPHONE, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_FAX, { scope: CustomerExpandEnum.PHONE_NUMBERS, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_EMAIL, { scope: CustomerExpandEnum.EMAIL_ADDRESSES, method: MainEntityFieldInjectionMethods.EXPAND }],
  [EntityFieldCodeEnum.CUSTOMER_EMAIL_2, { scope: CustomerExpandEnum.EMAIL_ADDRESSES, method: MainEntityFieldInjectionMethods.EXPAND }],
])

export type CustomerFieldConfig = {
  includes?: Set<CustomerIncludeEnum>
  expands?: Set<CustomerExpandEnum>
}
